import firebase from "firebase/compat/app";
import { firestore } from "../firebase/firebase.config";
import { Challenge } from "@/models/database/challenges/challenge.model";
import { ChallengeAdditionalData } from "@/models/database/challenges/challengeAdditionalData.model";
import { JunctionProfileChallenge } from "@/models/database/challenges/junctionProfileChallenge.model";
import { News } from "@/models/database/news/News.model";
import { NewsActivity } from "@/models/database/news/newsActivity.model";
import { Brand } from "@/models/database/partners/HP/Brand.model";
import { Training } from "@/models/database/partners/HP/Training.model";
import { Post } from "@/models/database/posts/post.model";
import { Comment } from "@/models/database/posts/postComment.model";
import { Like } from "@/models/database/posts/postLike.model";
import { Profile } from "@/models/database/profiles/profile.model";
import { UsersFollowers } from "@/models/database/profiles/usersFollowers.model";
import { Report } from "@/models/database/reports/reports";
import { Submission } from "@/models/database/submissions/Submission.model";
import { Team } from "@/models/database/teams/team.model";
import { TeamFollowers } from "@/models/database/teams/teamsFollowers.model";
import { TeamTournamentJunction } from "@/models/database/teams/teamTournamentJunction.model";
import { UserTeamJunction } from "@/models/database/teams/userTeamJunction.model";
import { Tournament } from "@/models/database/tournaments/tournament.model";
import { Games } from "@/models/database/games/games.model";
import { globalLeaderboard } from "@/features/leaderboard/models/leaderboardProps";
import { profileLunarProps } from "@/features/profile/types/profileLunar";
import { Zones } from "@/models/database/zones/Zones.model";
import { Events } from "@/models/database/event/Events.model";

/*
returns the firebase collection reference with type.
*/
const collectionRefWithType = <Type>(collectionName: string) =>
  firestore.collection(
    collectionName
  ) as firebase.firestore.CollectionReference<Type>;

// for comments/likes as subcollection for posts.
// type P -> Type of parent collection
const subCollectionRefWithType = <T, P>(
  postId: string,
  subCollectionName: "comments" | "likes" | string,
  parentCollectionName: string
) =>
  collectionRefWithType<P>(parentCollectionName)
    .doc(postId)
    .collection(subCollectionName) as firebase.firestore.CollectionReference<T>;

export const collectionRef = {
  admin: collectionRefWithType<unknown>("admin"),
  challenge: collectionRefWithType<Challenge>("challenges"),
  leaderboard: collectionRefWithType<globalLeaderboard>("globalLeaderboard"),
  profileLunar: collectionRefWithType<profileLunarProps>("profileLunar"),
  junctionProfileChallenge: collectionRefWithType<JunctionProfileChallenge>(
    "junction_profile_challenge"
  ),
  posts: collectionRefWithType<Post>("posts"),
  userFollowers: collectionRefWithType<UsersFollowers>("usersFollowers"),
  profiles: (tenantId: string) =>
    subCollectionRefWithType<Profile, any>(tenantId, "records", "profiles"),
  profile: (userId: string, tenantId: string) =>
    collectionRefWithType<Profile>("profiles")
      .doc(tenantId)
      .collection("records")
      .doc(userId),

  challengeFieldsToCompare: collectionRefWithType<ChallengeAdditionalData>(
    "challengesFieldsToCompare"
  ),
  comments: (postId: string) =>
    subCollectionRefWithType<Comment, Post>(postId, "comments", "posts"),
  likes: (postId: string) =>
    subCollectionRefWithType<Like, Post>(postId, "likes", "posts"),

  // submissions: collectionRefWithType<Submission>("submissions"),

  submissions: (tenantId: string) =>
    subCollectionRefWithType<Submission, any>(
      tenantId,
      "records",
      "submissions"
    ),

  precisionGame: (tenantId: string, date: string) =>
    subCollectionRefWithType<any, any>(tenantId, date, "precisionGame"),

  match_results: (tenantId: string) =>
    subCollectionRefWithType<any, any>(tenantId, "records", "match_results"),

  teams: collectionRefWithType<Team>("teams"),
  userTeamJunction: collectionRefWithType<UserTeamJunction>("userTeamJunction"),
  teamsFollowers: collectionRefWithType<TeamFollowers>("teamsFollowers"),
  teamTournamentJunction: collectionRefWithType<TeamTournamentJunction>(
    "teamTournamentJunction"
  ),
  games: collectionRefWithType<Games>("games"),
  tournaments: collectionRefWithType<Tournament>("tournaments"),
  news: (tenantId: string) =>
    subCollectionRefWithType<News, any>(tenantId, "articles", "news"),
  events: (tenantId: string) =>
    subCollectionRefWithType<Events, any>(tenantId, "event", "events"),
  zones: (tenantId: string) =>
    subCollectionRefWithType<Zones, any>(tenantId, "partners", "zones"),
  newsActivity: collectionRefWithType<NewsActivity>("newsActivity"),
  notifications: collectionRefWithType<Notification>("notifications"),
  winners1337: collectionRefWithType<any>("1337winners"),
  reports: subCollectionRefWithType<Report, null>(
    "reports",
    "reports",
    "admin"
  ),
  partners: {
    hp: {
      brands: collectionRefWithType<Brand>("retail_products")
        .doc("hp")
        .collection("brands"),
      products: collectionRefWithType<Training>("retail_products")
        .doc("hp")
        .collection("products"),
    },
  },
};
// ---------------------------------------------------------------------
// For fetching single document
async function getDocs<T>(
  dataToBeFetched: firebase.firestore.CollectionReference<T>
) {
  const dataFetched = await dataToBeFetched.get();
  return dataFetched.docs.map(
    (x: firebase.firestore.QueryDocumentSnapshot<T>) => x.data()
  );
}

export const getDataFromQuery = {
  challenge: (query: firebase.firestore.CollectionReference<Challenge>) =>
    getDocs<Challenge>(query),
  profile: (query: firebase.firestore.CollectionReference<Profile>) =>
    getDocs<Profile>(query),
  posts: (query: firebase.firestore.CollectionReference<Post>) =>
    getDocs<Post>(query),
  userFollowers: (
    query: firebase.firestore.CollectionReference<UsersFollowers>
  ) => getDocs<UsersFollowers>(query),
  Junction_profile_challenge: (
    query: firebase.firestore.CollectionReference<JunctionProfileChallenge>
  ) => getDocs<JunctionProfileChallenge>(query),
  news: (query: firebase.firestore.CollectionReference<News>) =>
    getDocs<News>(query),
};

const collectionDocSnapshot = <Type>(
  docId: string,
  docRef: firebase.firestore.CollectionReference<Type>
) => {
  return docRef.doc(docId).get();
};

const subCollectionDocSnapshot = <Type>(
  docRef: firebase.firestore.CollectionReference<Type>, // "profiles"
  parentId: string, // "tenant"
  docId: string, // "records"
  id: string // "uid"
) => {
  return docRef.doc(parentId).collection(docId).doc(id).get();
};

const commentDocSnapshot = (postId: string, docId: string) => {
  return collectionRef.comments(postId).doc(docId).get();
};
export const collectionDoc = {
  /**
   * Returns the firebase document snapshot with dedicated type of the collection.
   * @param {string} docId challenge id
   * @returns Promise<firebase.firestore.DocumentSnapshot<Challenge>>
   */
  challenge: (docId: string) =>
    collectionDocSnapshot<Challenge>(docId, collectionRef.challenge),
  /**
   * Returns the firebase document snapshot with dedicated type of the collection.
   * @param {string} docId junction profile challenge id
   * @returns Promise<firebase.firestore.DocumentSnapshot<JunctionProfileChallenge>>
   */
  junctionProfileChallenge: (docId: string) =>
    collectionDocSnapshot<JunctionProfileChallenge>(
      docId,
      collectionRef.junctionProfileChallenge
    ),
  /**
   * Returns the firebase document snapshot with dedicated type of the collection.
   * @param {string} docId post id
   * @returns Promise<firebase.firestore.DocumentSnapshot<Post>>
   */
  posts: (docId: string) =>
    collectionDocSnapshot<Post>(docId, collectionRef.posts),
  /**
   * Returns the firebase document snapshot with dedicated type of the collection.
   * @param {string} docId user follower id
   * @returns Promise<firebase.firestore.DocumentSnapshot<UsersFollowers>>
   */
  userFollowers: (docId: string) =>
    collectionDocSnapshot<UsersFollowers>(docId, collectionRef.userFollowers),
  /**
   * Returns the firebase document snapshot with dedicated type of the collection.
   * @param {string} docId challenge field to compare id
   * @returns Promise<firebase.firestore.DocumentSnapshot<ChallengeAdditionalData>>
   */
  challengeFieldsToCompare: (docId: string) =>
    collectionDocSnapshot<ChallengeAdditionalData>(
      docId,
      collectionRef.challengeFieldsToCompare
    ),
  /**
   * Returns the firebase document snapshot with dedicated type of the collection.
   * @param {string} docId profile id
   * @returns Promise<firebase.firestore.DocumentSnapshot<Profiles>>
   */
  profiles: (docId: string, tenantId: string) =>
    collectionDocSnapshot<Profile>(docId, collectionRef.profiles(tenantId)),
  // collectionRef.profile(docId, tenantId).get() as firebase.firestore.collectionDoc<Profile>,
  /**
   * Returns the firebase document snapshot with dedicated type of the collection.
   * @param {string} docId comment id
   * @returns Promise<firebase.firestore.DocumentSnapshot<Comment>>
   */
  comments: (postId: string, docId: string) =>
    commentDocSnapshot(postId, docId),

  news: (docId: string, tenantId: string) =>
    collectionDocSnapshot<News>(docId, collectionRef.news(tenantId)),

  newsActivity: (docId: string) =>
    collectionDocSnapshot<NewsActivity>(docId, collectionRef.newsActivity),

  leaderboard: (docId: string) =>
    collectionDocSnapshot(docId, collectionRef.leaderboard),

  profileLunar: (docId: string) =>
    collectionDocSnapshot(docId, collectionRef.profileLunar),
};
// ---------------------------------------------------------------------
// For fetching multiple docs when array of doc ids is provided

const getAllCollectionDocs = async <Type>(
  docIds: string[],
  collectionName: string
) => {
  const collectionRefOfAllIds: Promise<
    firebase.firestore.DocumentSnapshot<Type>
  >[] = [];
  for (const docId of docIds) {
    const doc = collectionRefWithType<Type>(collectionName).doc(docId).get();
    collectionRefOfAllIds.push(doc);
  }
  return await Promise.all(collectionRefOfAllIds);
};

const getAllCommentsBasedOnCommentIds = async (
  postId: string,
  docIds: string
) => {
  const collectionRefOfAllIds: Promise<
    firebase.firestore.DocumentSnapshot<Comment>
  >[] = [];
  for (const docId of docIds) {
    const doc = collectionDoc.comments(postId, docId);
    collectionRefOfAllIds.push(doc);
  }
  return await Promise.all(collectionRefOfAllIds);
};

export const getAllCollectionDocsBasedOfDocIds = {
  challenge: (docIds: string[]) =>
    getAllCollectionDocs<Challenge>(docIds, "challenges"),
  junctionProfileChallenge: (docIds: string[]) =>
    getAllCollectionDocs<JunctionProfileChallenge>(
      docIds,
      "junction_profile_challenge"
    ),
  posts: (docIds: string[]) => getAllCollectionDocs<Post>(docIds, "posts"),
  userFollowers: (docIds: string[]) =>
    getAllCollectionDocs<UsersFollowers>(docIds, "usersFollowers"),
  profiles: (docIds: string[]) =>
    getAllCollectionDocs<Profile>(docIds, "profiles"),
  comments: getAllCommentsBasedOnCommentIds,
};

// ---------------------------------------------------------------------
// For real-time updates using onSnapshot

/**
 * Creates a listener for a collection that calls the callback when data changes
 * @param collectionReference The Firestore collection reference to listen to
 * @param callback Function to call when data changes, receives the updated documents
 * @returns An unsubscribe function that can be called to stop listening
 */
export function subscribeToCollection<T>(
  collectionReference: firebase.firestore.CollectionReference<T>,
  callback: (data: T[]) => void
): () => void {
  return collectionReference.onSnapshot((snapshot) => {
    const data = snapshot.docs.map((doc) => doc.data());
    callback(data);
  });
}

/**
 * Creates a listener for a single document that calls the callback when data changes
 * @param documentReference The Firestore document reference to listen to
 * @param callback Function to call when data changes, receives the updated document
 * @returns An unsubscribe function that can be called to stop listening
 */
export function subscribeToDocument<T>(
  documentReference: firebase.firestore.DocumentReference<T>,
  callback: (data: T | null) => void
): () => void {
  return documentReference.onSnapshot((snapshot) => {
    callback(snapshot.exists ? (snapshot.data() as T) : null);
  });
}

// Collection-specific subscription helpers
export const collectionSubscriptions = {
  precisionGame: (
    tenantId: string,
    date: string,
    callback: (data: any[]) => void
  ) =>
    subscribeToCollection(
      collectionRef.precisionGame(tenantId, date),
      callback
    ),
  profiles: (tenantId: string, callback: (data: Profile[]) => void) =>
    subscribeToCollection(collectionRef.profiles(tenantId), callback),
};

// Document-specific subscription helpers
export const documentSubscriptions = {
  precisionGame: (
    tenantId: string,
    date: string,
    docId: string,
    callback: (data: any | null) => void
  ) =>
    subscribeToDocument(
      collectionRef.precisionGame(tenantId, date).doc(docId),
      callback
    ),
};
